import DropdownButton from "@/components/app/DropdownButton";
import { NavButton } from "@/components/app/NavButton";
import { ROUTE_PARAMS } from "@/constants";
import { getDealerAccountRouteId, useAccount } from "@/context/account";
import { useFeatureFlags } from "@/context/FeatureFlags";
import { useMe } from "@/context/me";
import type { SupportModeSession } from "@/context/supportMode";
import { useSupportMode } from "@/context/supportMode";
import { DealerUserRole, SupportViewType } from "@/gql/graphql";
import { UiFeatureFlagNames } from "@/gql/schema-types";
import { isDealerWithoutRole } from "@/permissions";
import { pageLayout } from "@/theme/pageLayout";
import type { AccordionProps, MenuItemProps } from "@mui/material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  MenuItem,
  Stack,
  styled,
  Toolbar,
  Typography,
} from "@mui/material";
import { Tooltip } from "@trimble/modus-cmx";
import { useTranslation } from "react-i18next";
import type { NavLinkProps, To } from "react-router";
import { matchPath, NavLink, useLocation } from "react-router";
import "./MainNavigation.scss";

type NavItem = {
  title: string;
  to?: To;
  value?: string;
  end?: boolean;
  children?: NavItem[];
  visibleWhenEnabled?: boolean;
  disabled?: boolean;
};

type AccountNavMenuItemProps = {
  title: string;
  to?: To;
  isDisabled: boolean;
  disabledTooltip: string;
  onClose?: () => void;
  sx?: MenuItemProps["sx"];
};

type NavDropdownNavItemProps = {
  navItem: NavItem;
  isDealerMembersActive: boolean;
};

const isAccountSwitchDisabledInSupportModeAsUser = (
  supportModeSession: SupportModeSession | null,
  selectedAccountId: string,
  accountId: string
): boolean =>
  supportModeSession !== null &&
  supportModeSession.viewType === SupportViewType.User &&
  supportModeSession.cdhId === selectedAccountId &&
  accountId !== selectedAccountId;

/** Truncate dealer name from lg until xl to avoid overlap with nav links. */
const accountNameButtonSx = {
  fontWeight: 700,
  fontSize: 18,
  pl: 0,
  minWidth: 0,
  maxWidth: { lg: 280, xl: "none" },
  "& .MuiButton-endIcon": {
    flexShrink: 0,
  },
};

function NavAccordionBase(props: AccordionProps) {
  return <Accordion disableGutters square {...props} />;
}

const NavAccordion = styled(NavAccordionBase)({
  color: "var(--modus-wc-color-white)",
  bgcolor: "inherit",
  "& .MuiAccordionSummary-expandIconWrapper": {
    color: "inherit",
  },
});

const LinkMenuItem = styled(
  ({
    to,
    end,
    ...rest
  }: MenuItemProps & Partial<Pick<NavLinkProps, "to" | "end">>) => {
    if (to !== undefined) {
      return <MenuItem component={NavLink} to={to} end={end} {...rest} />;
    }

    return <MenuItem {...rest} />;
  }
)(({ theme }) => ({
  borderLeft: `4px solid transparent`,
  "&.active": {
    borderLeftColor: theme.palette.secondary.main,
  },
}));

function AccountNavMenuItem({
  title,
  to,
  isDisabled,
  disabledTooltip,
  onClose,
  sx,
}: AccountNavMenuItemProps) {
  return (
    <Tooltip content={disabledTooltip} disabled={!isDisabled}>
      <Box component="span" sx={{ display: "block", width: "100%" }}>
        <LinkMenuItem
          sx={sx}
          to={isDisabled ? undefined : to}
          disabled={isDisabled}
          onClick={isDisabled ? undefined : onClose}
        >
          {title}
        </LinkMenuItem>
      </Box>
    </Tooltip>
  );
}

function NavDropdownNavItem({
  navItem,
  isDealerMembersActive,
}: NavDropdownNavItemProps) {
  const adminSlotProps = {
    button: {
      className: isDealerMembersActive ? "adminNavActiveBorder" : undefined,
    },
    menu: {
      slotProps: {
        paper: {
          className: "adminNavMenuPaper",
        },
        list: {
          className: "adminNavMenuList",
        },
      },
    },
  };

  return (
    <DropdownButton title={navItem.title} slotProps={adminSlotProps}>
      {({ onClose }) =>
        (navItem.children ?? [])
          .filter(
            (child) =>
              child.to !== undefined && child.visibleWhenEnabled !== false
          )
          .map((child) => (
            <LinkMenuItem
              key={child.title}
              to={child.to}
              onClick={onClose}
              className="adminNavMenuItem"
            >
              {child.title}
            </LinkMenuItem>
          ))
      }
    </DropdownButton>
  );
}

function MainNavigation() {
  const { t } = useTranslation();

  const {
    accounts,
    accountsLoading,
    account,
    getAccountPath,
    getAccountDashboardPath,
  } = useAccount();

  const { canUseFeatureFlag, features } = useFeatureFlags();
  const { me } = useMe();
  const { session: supportModeSession } = useSupportMode();
  const { pathname } = useLocation();

  const isAccountSwitchDisabled = (accountId: string) =>
    isAccountSwitchDisabledInSupportModeAsUser(
      supportModeSession,
      account.id,
      accountId
    );
  const disabledAccountTooltip = t(
    "super_admin.support_mode.account_switch_limit_tooltip"
  );
  const isDealerMembersPage = matchPath(
    { path: `/:${ROUTE_PARAMS.ACCOUNT_ID}/dealer-members`, end: true },
    pathname
  );
  const dealerWithoutRole = isDealerWithoutRole(me.role, me.permissionList);
  const navItems: NavItem[] = dealerWithoutRole
    ? []
    : [
        {
          title: t("subnav_items.title.dashboard"),
          to: "",
          end: true,
          visibleWhenEnabled: true,
        },
        {
          title: t("subnav_items.title.dealer"),
          to: "./dealer",
          visibleWhenEnabled: features.DEALER_PAGE,
        },
        {
          title: t("subnav_items.title.customers"),
          to: "./customers",
          visibleWhenEnabled: true,
        },
        {
          title: t("subnav_items.title.devices"),
          to: "./devices",
          visibleWhenEnabled: features.DEVICE_LIST,
        },
        {
          title: t("subnav_items.title.contracts"),
          to: "./contracts",
          visibleWhenEnabled: true,
        },
        {
          title: t("subnav_items.title.invoices"),
          to: "./invoices",
          visibleWhenEnabled: true,
        },
        {
          title: t("subnav_items.title.admin"),
          visibleWhenEnabled: true,
          children: [
            {
              title: t("subnav_items.admin_menu.dealer_members"),
              to: "./dealer-members",
            },
            {
              title: t("subnav_items.admin_menu.audit_logs"),
              to: "./audit-logs",
              visibleWhenEnabled: true,
            },
            {
              title: t("product_pricelist.title"),
              to: "./product-pricelist",
              visibleWhenEnabled:
                canUseFeatureFlag(UiFeatureFlagNames.ExportPriceList) &&
                (me.role === DealerUserRole.DealerOwner ||
                  me.role === DealerUserRole.DealerSecondaryOwner),
            },
          ],
        },
      ].filter((navItem) => navItem.visibleWhenEnabled);
  const accountMenuItems = accounts.map((mappedAccount) => ({
    id: mappedAccount.id,
    iamId: mappedAccount.iamId,
    title: mappedAccount.companyName,
    isDisabled: isAccountSwitchDisabled(mappedAccount.id),
  }));
  const accordionItems: NavItem[] = [
    {
      title: t("subnav_items.title.change_account"),
      children: accountMenuItems.map((item) => ({
        title: item.title,
        to: getAccountPath(
          getDealerAccountRouteId({ id: item.id, iamId: item.iamId })
        ),
        disabled: item.isDisabled,
      })),
    },
    ...navItems,
  ];

  return (
    <>
      {/* lg+ navbar */}
      <Toolbar
        disableGutters
        sx={{
          bgcolor: "trimbleGray.main",
          display: { xs: "none", lg: "flex" },
          height: 56,
        }}
        variant="dense"
      >
        <Box sx={pageLayout}>
          <Stack direction="row">
            <Box
              flexGrow={1}
              minWidth={0}
              sx={{
                maxWidth: { lg: 280, xl: "none" },
              }}
            >
              {accounts.length > 1 ? (
                <Tooltip content={account.companyName} position="bottom">
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      minWidth: 0,
                      maxWidth: "100%",
                    }}
                  >
                    <DropdownButton
                      title={account.companyName}
                      slotProps={{
                        button: {
                          sx: accountNameButtonSx,
                        },
                      }}
                    >
                      {({ onClose }) =>
                        accountMenuItems.map((item) => (
                          <AccountNavMenuItem
                            key={item.id}
                            title={item.title}
                            to={getAccountDashboardPath(
                              getDealerAccountRouteId({
                                id: item.id,
                                iamId: item.iamId,
                              })
                            )}
                            isDisabled={item.isDisabled}
                            disabledTooltip={disabledAccountTooltip}
                            onClose={onClose}
                          />
                        ))
                      }
                    </DropdownButton>
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip
                  content={accounts[0]?.companyName ?? ""}
                  position="bottom"
                >
                  <Box
                    component="span"
                    sx={{
                      display: "inline-flex",
                      minWidth: 0,
                      maxWidth: "100%",
                    }}
                  >
                    <NavButton
                      loading={accountsLoading}
                      loadingPosition="end"
                      sx={{
                        ...accountNameButtonSx,
                        "&.MuiButton-loading": {
                          color: "var(--modus-wc-color-white)",
                        },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          minWidth: 0,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {accounts[0]?.companyName}
                      </Box>
                    </NavButton>
                  </Box>
                </Tooltip>
              )}
            </Box>
            <Stack direction="row" gap={2}>
              {navItems.map((navItem) =>
                navItem.children ? (
                  <NavDropdownNavItem
                    key={navItem.title}
                    navItem={navItem}
                    isDealerMembersActive={isDealerMembersPage != null}
                  />
                ) : (
                  <NavButton
                    key={navItem.title}
                    to={navItem.to}
                    end={navItem.end}
                  >
                    {navItem.title}
                  </NavButton>
                )
              )}
            </Stack>
          </Stack>
        </Box>
      </Toolbar>
      {/* xs–md navbar */}
      <NavAccordion
        sx={{
          display: { xs: "block", lg: "none" },
          bgcolor: "var(--modus-wc-color-gray-10)",
        }}
      >
        <AccordionSummary sx={{ height: 56 }}>
          <Typography fontWeight={700} fontSize={18}>
            {account.companyName}
          </Typography>
        </AccordionSummary>
        <AccordionDetails
          sx={{ bgcolor: "var(--modus-wc-color-gray-9)", p: 0 }}
        >
          {accordionItems.map((accordionItem) =>
            accordionItem.children ? (
              <NavAccordion key={accordionItem.title}>
                <AccordionSummary
                  sx={{ bgcolor: "var(--modus-wc-color-gray-9)" }}
                >
                  <Typography>{accordionItem.title}</Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    bgcolor: "var(--modus-wc-color-white)",
                    color: "var(--modus-wc-color-black)",
                    p: 0,
                    py: 2,
                  }}
                >
                  {accordionItem.children
                    .filter(
                      (childAccordionItem) =>
                        childAccordionItem.visibleWhenEnabled !== false
                    )
                    .map((childAccordionItem) => (
                      <AccountNavMenuItem
                        key={`${accordionItem.title}-${String(childAccordionItem.to ?? childAccordionItem.title)}`}
                        sx={{ height: 48 }}
                        title={childAccordionItem.title}
                        to={childAccordionItem.to}
                        isDisabled={childAccordionItem.disabled === true}
                        disabledTooltip={disabledAccountTooltip}
                      />
                    ))}
                </AccordionDetails>
              </NavAccordion>
            ) : (
              <LinkMenuItem
                key={accordionItem.title}
                sx={{ height: 48 }}
                to={accordionItem.to}
                end={accordionItem.end}
              >
                {accordionItem.title}
              </LinkMenuItem>
            )
          )}
        </AccordionDetails>
      </NavAccordion>
    </>
  );
}

export default MainNavigation;
